<template>
    <!--
        USER PREFERENCES - REPORT SECTION

        Preferences affecting what is generated into the patient report.

        NOTE: this component only edits the values, the parent dialog owns loading and saving them.
              Every control emits 'on-pref-change' with its field name so the parent can persist it.
    -->
    <div>
        <div class="bntx-prefs-detail-title">Report</div>

        <!-- ========================== -->
        <!--    EMG                     -->
        <!-- ========================== -->
        <Checkbox id="user_prefs_report_auto_include_emg_compliance_text"
                  v-model="preferences.autoIncludeEmgComplianceText"
                  @on-change="$emit('on-pref-change', 'autoIncludeEmgComplianceText')">
            Auto-include EMG compliance text
        </Checkbox>

        <div class="bntx-prefs-detail-hint">
            Added to the report when EMG injections are detected in the session.
        </div>

        <div class="bntx-prefs-field-label-row">
            <span class="bntx-prefs-field-label">EMG compliance text</span>
            <Button id="user_prefs_report_reset_emg_compliance_text"
                    size="small"
                    :disabled="preferences.autoIncludeEmgComplianceText == false || isAtDefault('emgComplianceText', USER_PREF.REPORT_EMG_COMPLIANCE_TEXT)"
                    @click="resetToDefault('emgComplianceText', USER_PREF.REPORT_EMG_COMPLIANCE_TEXT)">Reset to default</Button>
        </div>

        <Input element-id="user_prefs_report_emg_compliance_text"
               v-model="preferences.emgComplianceText"
               type="textarea"
               :rows="3"
               :disabled="preferences.autoIncludeEmgComplianceText == false"
               placeholder="EMG compliance text to include in the report"
               @on-change="$emit('on-pref-change', 'emgComplianceText')" />

        <div class="bntx-prefs-section-divider"></div>

        <!-- ========================== -->
        <!--    ULTRASOUND              -->
        <!-- ========================== -->
        <Checkbox id="user_prefs_report_auto_include_ultrasound_compliance_text"
                  v-model="preferences.autoIncludeUltrasoundComplianceText"
                  @on-change="$emit('on-pref-change', 'autoIncludeUltrasoundComplianceText')">
            Auto-include Ultrasound compliance text
        </Checkbox>

        <div class="bntx-prefs-detail-hint">
            Added to the report when ultrasound injections are detected in the session.
        </div>

        <div class="bntx-prefs-field-label-row">
            <span class="bntx-prefs-field-label">Ultrasound compliance text</span>
            <Button id="user_prefs_report_reset_ultrasound_compliance_text"
                    size="small"
                    :disabled="preferences.autoIncludeUltrasoundComplianceText == false || isAtDefault('ultrasoundComplianceText', USER_PREF.REPORT_ULTRASOUND_COMPLIANCE_TEXT)"
                    @click="resetToDefault('ultrasoundComplianceText', USER_PREF.REPORT_ULTRASOUND_COMPLIANCE_TEXT)">Reset to default</Button>
        </div>

        <Input element-id="user_prefs_report_ultrasound_compliance_text"
               v-model="preferences.ultrasoundComplianceText"
               type="textarea"
               :rows="3"
               :disabled="preferences.autoIncludeUltrasoundComplianceText == false"
               placeholder="Ultrasound compliance text to include in the report"
               @on-change="$emit('on-pref-change', 'ultrasoundComplianceText')" />

        <div class="bntx-prefs-section-divider"></div>

        <!-- ================================ -->
        <!--    EMG + ULTRASOUND (COMBINED)   -->
        <!-- ================================ -->
        <Checkbox id="user_prefs_report_auto_include_emg_and_ultrasound_compliance_text"
                  v-model="preferences.autoIncludeEmgAndUltrasoundComplianceText"
                  @on-change="$emit('on-pref-change', 'autoIncludeEmgAndUltrasoundComplianceText')">
            Auto-include EMG and Ultrasound compliance text
        </Checkbox>

        <div class="bntx-prefs-detail-hint">
            Added to the report when an injection used both EMG and ultrasound guidance together (the star marker).
        </div>

        <div class="bntx-prefs-field-label-row">
            <span class="bntx-prefs-field-label">EMG and Ultrasound compliance text</span>
            <Button id="user_prefs_report_reset_emg_and_ultrasound_compliance_text"
                    size="small"
                    :disabled="preferences.autoIncludeEmgAndUltrasoundComplianceText == false || isAtDefault('emgAndUltrasoundComplianceText', USER_PREF.REPORT_EMG_AND_ULTRASOUND_COMPLIANCE_TEXT)"
                    @click="resetToDefault('emgAndUltrasoundComplianceText', USER_PREF.REPORT_EMG_AND_ULTRASOUND_COMPLIANCE_TEXT)">Reset to default</Button>
        </div>

        <Input element-id="user_prefs_report_emg_and_ultrasound_compliance_text"
               v-model="preferences.emgAndUltrasoundComplianceText"
               type="textarea"
               :rows="3"
               :disabled="preferences.autoIncludeEmgAndUltrasoundComplianceText == false"
               placeholder="Compliance text to include in the report when both EMG and ultrasound were used together"
               @on-change="$emit('on-pref-change', 'emgAndUltrasoundComplianceText')" />
    </div>
</template>

<script>

import { USER_PREF } from '@/js/user-prefs-keys';

export default {
    name: 'user-preferences-report',

    props: {
        // the `report` slice of the preferences object owned by the parent dialog
        preferences: {
            type: Object,
            required: true
        }
    },

    data () {
        return {
            // exposed to the template so the reset buttons can name the built-in default they restore
            USER_PREF : USER_PREF
        }
    },

    methods: {

        isAtDefault (inFieldName, inPref) {
            return this.preferences[inFieldName] == inPref.defaultValue
        },

        /*
            Puts the built-in default text back, for when the user has deleted or mangled their own
            wording - and, since getUserPref only hands out the default to users who have never had
            the key created, it is also how an existing user picks up a changed default.

            Emitting on-pref-change makes the parent persist it exactly as if it had been typed.
        */
        resetToDefault (inFieldName, inPref) {
            this.preferences[inFieldName] = inPref.defaultValue

            this.$emit('on-pref-change', inFieldName)
        }
    }
}
</script>
